import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { useUserStore } from '@/store/userStore';
import { useTelegram } from './useTelegram';
import type { Profile } from '@/types';

/**
 * Определяет timezone устройства юзера
 */
function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Moscow';
  } catch {
    return 'Europe/Moscow';
  }
}

/**
 * Обновляет timezone в профиле если он изменился
 */
async function syncTimezone(profileId: string, currentTimezone: string | null | undefined) {
  const deviceTimezone = detectTimezone();
  if (currentTimezone === deviceTimezone) return;
  console.log('[auth] Updating timezone:', currentTimezone, '→', deviceTimezone);
  try {
    await supabase
      .from('profiles')
      .update({ timezone: deviceTimezone })
      .eq('id', profileId);
  } catch (e) {
    console.error('[auth] Failed to update timezone:', e);
  }
}

/**
 * Является ли email "техническим" от tg-auth (tg_XXX@caltrack.app)
 */
function isTelegramAuthEmail(email: string | null | undefined): boolean {
  if (!email) return false;
  return email.endsWith('@caltrack.app');
}

export function useAuth() {
  const { initData, user: tgUser } = useTelegram();
  const { setProfile, setLoading, setAuthStuck } = useUserStore();
  
  const webLoadInProgressRef = useRef<string | null>(null);
  
  useEffect(() => {
    let mounted = true;
    let authTimeoutId: ReturnType<typeof setTimeout> | null = null;
    
    if (initData) {
      authTimeoutId = setTimeout(() => {
        if (mounted) {
          console.warn('[auth] Telegram auth timeout — showing email fallback');
          setAuthStuck(true);
          setLoading(false);
        }
      }, 15000);
    }
    
    async function bootstrap() {
      setLoading(true);
      setAuthStuck(false);
      try {
        let session = (await supabase.auth.getSession()).data.session;
        
        // ═══════════════════════════════════════════════════════
        // TELEGRAM FLOW (есть initData)
        // ═══════════════════════════════════════════════════════
        if (initData) {
          if (!session) {
            const res = await fetch(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/tg-auth`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ initData }),
              },
            );
            if (!res.ok) throw new Error('Telegram auth failed');
            const { access_token, refresh_token } = await res.json();
            await supabase.auth.setSession({ access_token, refresh_token });
            session = (await supabase.auth.getSession()).data.session;
          }
          
          if (!session?.user) {
            console.error('[auth] No session after tg-auth');
            return;
          }
          
          // 🆕 БЛОКИРОВКА: если у session.user.email НЕ tg_xxx@caltrack.app
          // → значит юзер залогинен через настоящий email (magic link)
          // → не пускаем в Telegram-режим
          const sessionEmail = session.user.email;
          if (sessionEmail && !isTelegramAuthEmail(sessionEmail)) {
            console.warn('[auth] BLOCK: email-юзер пытается войти в Telegram');
            alert(
              'Этот аккаунт работает через браузер.\n\n' +
              'Открой https://slavahealth.ru и войди через email.'
            );
            await supabase.auth.signOut();
            if (mounted) {
              setProfile(null as any);
              setLoading(false);
            }
            return;
          }
          
          // Ищем профиль по tg_id (приоритет)
          let profile: Profile | null = null;
          if (tgUser?.id) {
            const { data: byTgId } = await supabase
              .from('profiles')
              .select('*')
              .eq('tg_id', tgUser.id)
              .maybeSingle();
            if (byTgId) profile = byTgId as Profile;
          }
          
          // Иначе по auth.id
          if (!profile) {
            const { data: byId } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', session.user.id)
              .maybeSingle();
            if (byId) profile = byId as Profile;
          }
          
          // Создаём новый профиль если нет
          if (!profile) {
            const deviceTimezone = detectTimezone();
            const { data: created, error } = await supabase
              .from('profiles')
              .insert({
                id: session.user.id,
                tg_id: tgUser?.id,
                tg_username: tgUser?.username,
                first_name: tgUser?.first_name ?? 'Пользователь',
                timezone: deviceTimezone,
                onboarded: false,
                subscription_status: 'trial',
                subscription_expires_at: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
              })
              .select()
              .single();
            if (error) {
              console.error('[auth] Profile creation error:', error);
              throw error;
            }
            profile = created as Profile;
          } else {
            await syncTimezone(profile.id, (profile as any).timezone);
          }
          
          if (mounted) setProfile(profile);
          return;
        }
        
        // ═══════════════════════════════════════════════════════
        // WEB FLOW (Magic Link, нет initData)
        // ═══════════════════════════════════════════════════════
        if (session && !initData) {
          await loadOrCreateWebProfile(session, mounted);
          return;
        }
        
        // Нет ни сессии, ни Telegram — экран входа через email
        if (mounted) {
          setProfile(null as any);
          setLoading(false);
        }
      } catch (e) {
        console.error('[auth]', e);
        if (mounted && initData) {
          setAuthStuck(true);
        }
      } finally {
        if (mounted) setLoading(false);
        if (authTimeoutId) {
          clearTimeout(authTimeoutId);
          authTimeoutId = null;
        }
      }
    }
    
    async function loadOrCreateWebProfile(session: any, isMounted: boolean) {
      const userEmail = session.user.email;
      const authId = session.user.id;

      // Защита от параллельного вызова
      if (webLoadInProgressRef.current === authId) {
        console.log('[auth] Skip — already processing authId:', authId);
        return;
      }
      webLoadInProgressRef.current = authId;
      
      try {
        console.log('[auth] Web login as:', userEmail, 'authId:', authId);
        
        // 🆕 БЛОКИРОВКА: если email = tg_xxx@caltrack.app → это Telegram-юзер
        // (не должно случиться через magic link, но защитимся)
        if (isTelegramAuthEmail(userEmail)) {
          console.warn('[auth] BLOCK: Telegram-email в браузере');
          alert(
            'Этот аккаунт работает через Telegram.\n\n' +
            'Открой бот @kokorineatbot'
          );
          await supabase.auth.signOut();
          if (isMounted) {
            setProfile(null as any);
            setLoading(false);
          }
          return;
        }
        
        // Ищем существующий профиль по auth.id
        const { data: byId } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', authId)
          .maybeSingle();
        
        if (byId) {
          // 🆕 БЛОКИРОВКА: если у профиля есть tg_id → это Telegram-юзер
          // (он не должен входить через браузер)
          if ((byId as any).tg_id) {
            console.warn('[auth] BLOCK: профиль с tg_id пытается войти через браузер');
            alert(
              'Этот аккаунт привязан к Telegram.\n\n' +
              'Открой бот @kokorineatbot'
            );
            await supabase.auth.signOut();
            if (isMounted) {
              setProfile(null as any);
              setLoading(false);
            }
            return;
          }
          
          // Обновляем email если ещё не привязан
          if (!byId.email && userEmail) {
            const { data: updated } = await supabase
              .from('profiles')
              .update({ email: userEmail })
              .eq('id', authId)
              .select()
              .single();
            await syncTimezone(authId, (updated as any)?.timezone || (byId as any).timezone);
            if (isMounted) setProfile((updated || byId) as Profile);
          } else {
            await syncTimezone(authId, (byId as any).timezone);
            if (isMounted) setProfile(byId as Profile);
          }
          return;
        }
        
        // Поиск по email — чтобы найти юзера если auth.id отличается от profile.id
        let existingProfileByEmail: Profile | null = null;
        if (userEmail) {
          const { data: profiles } = await supabase
            .rpc('find_profile_by_email', { p_email: userEmail });
          if (profiles && profiles.length > 0) {
            existingProfileByEmail = profiles[0] as Profile;
          }
        }
        
        // 🆕 Если нашли по email — проверим что это НЕ Telegram-юзер
        if (existingProfileByEmail) {
          if ((existingProfileByEmail as any).tg_id) {
            console.warn('[auth] BLOCK: email привязан к Telegram-юзеру');
            alert(
              'Этот email привязан к Telegram-аккаунту.\n\n' +
              'Открой бот @kokorineatbot'
            );
            await supabase.auth.signOut();
            if (isMounted) {
              setProfile(null as any);
              setLoading(false);
            }
            return;
          }
          
          // Это email-юзер с email но id не совпадает с authId
          // → используем его (RLS будет работать т.к. profile.id = old, auth.uid = new)
          // ⚠️ В нормальной ситуации этого не должно быть для НОВЫХ юзеров
          await syncTimezone(existingProfileByEmail.id, (existingProfileByEmail as any).timezone);
          if (isMounted) setProfile(existingProfileByEmail);
          return;
        }
        
        // Создаём новый email-юзер
        const { data: created, error } = await supabase
          .from('profiles')
          .insert({
            id: authId,
            email: userEmail,
            first_name: userEmail?.split('@')[0] ?? 'Пользователь',
            timezone: detectTimezone(),
            onboarded: false,
            subscription_status: 'trial',
            subscription_expires_at: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
          })
          .select()
          .single();
        
        if (error) {
          console.error('[auth] Web profile creation error:', error);
          return;
        }
        if (isMounted) setProfile(created as Profile);
      } finally {
        webLoadInProgressRef.current = null;
      }
    }
    
    bootstrap();
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session && !initData) {
        loadOrCreateWebProfile(session, mounted);
      }
    });
    
    return () => {
      mounted = false;
      subscription.subscription.unsubscribe();
      if (authTimeoutId) {
        clearTimeout(authTimeoutId);
      }
    };
  }, [initData, tgUser?.id, setLoading, setProfile, setAuthStuck, tgUser?.first_name, tgUser?.username]);
}
